import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Calculator, ExternalLink, Star, FileText, Trash2 } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { BuyBoxSubnav } from "@/components/buy-box/buy-box-subnav";
import { BuyBoxAgentTags } from "@/components/buy-box/buy-box-agent-tags";
import type { BuyBoxAgentTag } from "@shared/schema";

type Favorite = {
  id: number;
  listingId: string;
  sourceReportId?: number | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  price?: string | null;
  sqft?: number | null;
  lotSize?: number | null;
  garageSpaces?: number | null;
  convertedBedrooms?: number | null;
  convertedBathrooms?: number | null;
  imageUrl?: string | null;
  propertyType?: string | null;
  listingUrl?: string | null;
  monthlyCashFlow?: string | null;
  cocReturn?: string | null;
  totalCashInvested?: string | null;
  annualTax?: string | null;
  annualTaxIsEstimate?: boolean | null;
  monthlyHoa?: string | null;
  description?: string | null;
  agentTags?: BuyBoxAgentTag[] | null;
  createdAt: string;
};

function fmtCurrency(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtPct(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}%`;
}

export default function AppBuyBoxSaved() {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  usePageTitle("Saved Properties");
  const { toast } = useToast();
  const favoritesQuery = useQuery<Favorite[]>({
    queryKey: ["/api/app/buy-box/favorites"],
  });

  const createUnderwritingMutation = useMutation({
    mutationFn: async (fav: Favorite) => {
      const zipCode = (() => {
        if (fav.zipCode) return fav.zipCode;
        const m = (fav.address || "").match(/\b(\d{5})(?:-\d{4})?\b/);
        return m ? m[1] : "";
      })();
      const garageSpaces = fav.garageSpaces || 0;
      const garageSqft = garageSpaces > 0 ? garageSpaces * 200 : undefined;
      const propRes = await apiRequest("POST", "/api/app/properties", {
        name: fav.address || `${fav.city || ""}, ${fav.state || ""}`,
        address: fav.address || "",
        city: fav.city || "",
        state: fav.state || "",
        zipCode,
        roomCount: fav.convertedBedrooms || 0,
        bathroomCount: fav.convertedBathrooms || 0,
        purchasePrice: fav.price ? String(fav.price) : "",
        sqft: fav.sqft ? String(fav.sqft) : "",
        propertyType: fav.propertyType || "",
        zillowUrl: fav.listingUrl || "",
        status: "underwriting",
        userId: 0,
        mainImage: fav.imageUrl || undefined,
        zillowImport: {
          listingLink: fav.listingUrl || undefined,
          sqft: fav.sqft || undefined,
          futureBedrooms: fav.convertedBedrooms || undefined,
          futureBathrooms: fav.convertedBathrooms || undefined,
          garageSpaces: garageSpaces || undefined,
          garageSqft,
          noGarage: garageSpaces > 0 ? false : undefined,
          homePurchasePrice: fav.price ? String(fav.price) : undefined,
          zipCode: zipCode || undefined,
        },
      });
      const prop = await propRes.json();
      return prop.id as number;
    },
    onSuccess: (propertyId: number) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      navigate(`/app/underwriting/${propertyId}`);
    },
    onError: () => {
      toast({ title: "Failed to create underwriting record", variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (listingId: string) => {
      await apiRequest("DELETE", `/api/app/buy-box/favorites/${encodeURIComponent(listingId)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/favorites"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/favorites/ids"] });
      toast({ title: "Removed from Favorites" });
    },
    onError: () => {
      toast({ title: "Could not remove favorite", variant: "destructive" });
    },
  });

  const favorites = favoritesQuery.data || [];

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link href="/app/buy-box/reports">
              <a className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1" data-testid="link-back-to-reports">
                <ArrowLeft className="h-3 w-3" /> Back to Reports
              </a>
            </Link>
            <h1
              className="text-2xl font-semibold text-gray-900 mt-1 flex items-center gap-2"
              data-testid="heading-saved-favorites"
              data-tour="buy-box-saved-heading"
            >
              <Star className="h-5 w-5 text-amber-500 fill-amber-400" /> Saved Favorites
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Listings you've starred across all your Buy Box reports.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="buy-box-saved" />
            <GuideButton pageKey="buy-box-saved" />
          </div>
        </div>

        <BuyBoxSubnav />

        <GuideNudgeBanner pageKey="buy-box-saved" />

        {favoritesQuery.isLoading ? (
          <div className="space-y-3" data-testid="loading-favorites">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center" data-testid="empty-favorites">
            <Star className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-600">
              No saved favorites yet. Open a Buy Box report and tap the
              <Star className="inline h-3.5 w-3.5 mx-1 text-amber-500" />
              star on any property to save it here.
            </p>
            <Link href="/app/buy-box/reports">
              <a className="inline-block mt-4">
                <Button size="sm" data-testid="button-view-reports">View Buy Box Reports</Button>
              </a>
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="favorites-list" data-tour="buy-box-saved-list">
            {favorites.map((fav, favIndex) => (
              <div
                key={fav.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                data-testid={`favorite-card-${fav.id}`}
              >
                <div className="flex gap-4 p-4">
                  {fav.imageUrl && (
                    <img
                      src={fav.imageUrl}
                      alt={fav.address || "Property"}
                      className="h-20 w-28 object-cover rounded-lg shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm truncate" data-testid={`text-fav-address-${fav.id}`}>
                          {fav.address || "Unknown address"}
                        </p>
                        <p className="text-xs text-gray-400 flex items-center gap-1.5 flex-wrap">
                          {fav.propertyType && <span>{fav.propertyType}</span>}
                          {fav.sqft != null && (
                            <>
                              {fav.propertyType && <span className="text-gray-300">·</span>}
                              <span>{fav.sqft.toLocaleString()} sqft</span>
                            </>
                          )}
                          {fav.garageSpaces != null && fav.garageSpaces > 0 && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span>{fav.garageSpaces}-car garage</span>
                            </>
                          )}
                          {fav.lotSize != null && fav.lotSize > 0 && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span data-testid={`text-fav-lot-size-${fav.id}`}>{fav.lotSize.toLocaleString()} sqft lot</span>
                            </>
                          )}
                        </p>
                        <BuyBoxAgentTags tags={fav.agentTags} testId={`tags-fav-${fav.id}`} />
                        {fav.description && (
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            {/cul[- ]de[- ]sac/i.test(fav.description) && (
                              <span
                                className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full border border-blue-200"
                                title="Listing description mentions cul-de-sac"
                                data-testid={`badge-fav-cul-de-sac-${fav.id}`}
                              >
                                Cul-de-Sac
                              </span>
                            )}
                            {/\bend\s+unit\b/i.test(fav.description) && (
                              <span
                                className="text-xs font-semibold bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full border border-purple-200"
                                title="Listing description mentions end unit"
                                data-testid={`badge-fav-end-unit-${fav.id}`}
                              >
                                End Unit
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeMutation.mutate(fav.listingId)}
                        disabled={removeMutation.isPending}
                        data-testid={`button-remove-favorite-${fav.id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      <div>
                        <div className="text-xs text-gray-400">Price</div>
                        <div className="font-semibold text-gray-900 text-sm" data-testid={`text-fav-price-${fav.id}`}>{fmtCurrency(fav.price)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">Beds / Baths</div>
                        <div className="font-semibold text-gray-900 text-sm">
                          {fav.convertedBedrooms ?? "—"}bd / {fav.convertedBathrooms ?? "—"}ba
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">Cash Flow</div>
                        <div className={`font-semibold text-sm ${parseFloat(String(fav.monthlyCashFlow || "0")) >= 0 ? "text-green-700" : "text-red-600"}`}>
                          {fmtCurrency(fav.monthlyCashFlow)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">CoC Return</div>
                        <div className="font-semibold text-gray-900 text-sm">{fmtPct(fav.cocReturn)}</div>
                      </div>
                    </div>

                    {(fav.annualTax != null || fav.monthlyHoa != null) && (
                      <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-gray-50">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">Annual Tax</div>
                          <div
                            className="font-medium text-gray-700 text-xs"
                            title={fav.annualTaxIsEstimate ? "City-average AI estimate." : undefined}
                          >
                            {fav.annualTax != null ? fmtCurrency(fav.annualTax) : "—"}
                            {fav.annualTaxIsEstimate && (
                              <span className="ml-1 text-[10px] text-amber-600 font-normal">(est.)</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">HOA</div>
                          <div className="font-medium text-gray-700 text-xs">
                            {fav.monthlyHoa != null && parseFloat(String(fav.monthlyHoa)) > 0
                              ? `${fmtCurrency(fav.monthlyHoa)}/mo`
                              : "None"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">All-in / Bed</div>
                          <div className="font-medium text-gray-700 text-xs">
                            {(() => {
                              const cash = parseFloat(String(fav.totalCashInvested || "0"));
                              const beds = fav.convertedBedrooms || 0;
                              if (cash > 0 && beds > 0) return `${fmtCurrency(cash / beds)}`;
                              return "—";
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="border-t border-gray-100 px-4 py-2 flex flex-wrap gap-2 bg-gray-50 items-center"
                  data-tour={favIndex === 0 ? "buy-box-saved-actions" : undefined}
                >
                  {fav.listingUrl && (
                    <a
                      href={fav.listingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`link-fav-listing-${fav.id}`}
                    >
                      <Button size="sm" variant="outline" className="text-xs">
                        <ExternalLink className="h-3 w-3 mr-1" /> View Listing
                      </Button>
                    </a>
                  )}
                  {fav.sourceReportId != null && (
                    <Link href={`/app/buy-box/reports/${fav.sourceReportId}`}>
                      <a data-testid={`link-fav-source-${fav.id}`}>
                        <Button size="sm" variant="outline" className="text-xs">
                          <FileText className="h-3 w-3 mr-1" /> Open Report
                        </Button>
                      </a>
                    </Link>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => createUnderwritingMutation.mutate(fav)}
                    disabled={createUnderwritingMutation.isPending && createUnderwritingMutation.variables?.id === fav.id}
                    data-testid={`button-open-underwriting-${fav.id}`}
                  >
                    <Calculator className="h-3 w-3 mr-1" />
                    {createUnderwritingMutation.isPending && createUnderwritingMutation.variables?.id === fav.id
                      ? "Opening…"
                      : "Open Underwriting"}
                  </Button>
                  <div className="ml-auto text-[11px] text-gray-400">
                    Saved {new Date(fav.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
